import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Plus, Tag, Trash2, Loader2, AlertCircle, Inbox } from 'lucide-react';

interface AliasModalProps {
  aliases: string[];
  /** Se rifiuta, il messaggio resta nel pannello e l'alias non viene aggiunto. */
  onAddAlias: (alias: string) => Promise<void>;
  onRemoveAlias: (alias: string) => Promise<void>;
  onClose: () => void;
  bankName: string;
}

/**
 * Alias con cui una banca compare nelle fonti.
 *
 * L'inserimento vive qui dentro invece che in una `Swal.fire` con `input`: cosi'
 * l'errore del server resta accanto al campo e la lista si aggiorna sotto gli occhi.
 */
const AliasModal: React.FC<AliasModalProps> = ({
  aliases,
  onAddAlias,
  onRemoveAlias,
  onClose,
  bankName,
}) => {
  const [nuovo, setNuovo] = useState('');
  const [errore, setErrore] = useState('');
  const [inCorso, setInCorso] = useState(false);
  /** Alias su cui è stata chiesta la rimozione: la conferma è sulla riga stessa. */
  const [daRimuovere, setDaRimuovere] = useState<string | null>(null);
  const [rimozioneInCorso, setRimozioneInCorso] = useState<string | null>(null);

  const aggiungi = async () => {
    const pulito = nuovo.trim();
    if (!pulito) {
      setErrore('Inserisci un alias.');
      return;
    }
    if (aliases.some((a) => a.toLowerCase() === pulito.toLowerCase())) {
      setErrore('Questo alias è già presente.');
      return;
    }

    setErrore('');
    setInCorso(true);
    try {
      await onAddAlias(pulito);
      setNuovo('');
    } catch (err) {
      setErrore(err instanceof Error ? err.message : 'Impossibile aggiungere l’alias.');
    } finally {
      setInCorso(false);
    }
  };

  const rimuovi = async (alias: string) => {
    setErrore('');
    setRimozioneInCorso(alias);
    try {
      await onRemoveAlias(alias);
      setDaRimuovere(null);
    } catch (err) {
      setErrore(err instanceof Error ? err.message : 'Impossibile rimuovere l’alias.');
    } finally {
      setRimozioneInCorso(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-modalOverlay flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.97, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.15 }}
        className="bg-surface rounded-2xl shadow-xl w-full max-w-md max-h-[80vh] flex flex-col"
      >
        {/* Intestazione */}
        <div className="flex items-start justify-between gap-3 p-5 border-b border-borderDivider">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center shrink-0">
              <Tag className="w-4 h-4 text-textPrimary/50" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-textPrimary">Alias</h3>
              <p className="text-[11px] text-textMuted truncate mt-0.5">{bankName}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 text-textMuted hover:text-textPrimary transition-colors"
            aria-label="Chiudi"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-4">
          {/* Inserimento */}
          <div className="flex flex-col gap-1.5">
            <div className="flex gap-2">
              <input
                type="text"
                value={nuovo}
                onChange={(e) => {
                  setNuovo(e.target.value);
                  if (errore) setErrore('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') aggiungi();
                }}
                placeholder="Nuovo alias…"
                disabled={inCorso}
                className="flex-1 px-3 py-2 rounded-lg border border-borderDivider text-sm text-textPrimary placeholder:text-textMuted focus:outline-none focus:border-textPrimary/50 disabled:opacity-50"
              />
              <button
                type="button"
                onClick={aggiungi}
                disabled={inCorso}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-textPrimary text-white hover:bg-textSecondary transition-colors disabled:opacity-50"
              >
                {inCorso ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Plus className="w-3.5 h-3.5" />
                )}
                Aggiungi
              </button>
            </div>
            <p className="text-[11px] text-textMuted">
              Nomi alternativi con cui questa banca compare nei documenti.
            </p>
          </div>

          {errore && (
            <div className="flex items-start gap-2 p-2.5 rounded-lg border border-red-200 bg-red-50 text-[11px] text-red-800">
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" />
              <span className="break-words">{errore}</span>
            </div>
          )}

          {/* Lista */}
          {aliases.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-8 text-center">
              <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center">
                <Inbox className="w-5 h-5 text-textPrimary/40" />
              </div>
              <p className="text-xs font-medium text-textPrimary">Nessun alias inserito</p>
            </div>
          ) : (
            <ul className="flex flex-col gap-2">
              {aliases.map((alias, i) => {
                const inConferma = daRimuovere === alias;
                const sta = rimozioneInCorso === alias;
                return (
                  <motion.li
                    key={alias}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.03 }}
                    className={`flex items-center gap-3 p-2.5 rounded-xl border transition-all ${
                      inConferma ? 'border-red-200 bg-red-50/50' : 'border-borderDivider bg-white'
                    }`}
                  >
                    <div
                      className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
                        inConferma ? 'bg-red-100' : 'bg-gray-50'
                      }`}
                    >
                      <Tag
                        className={`w-3.5 h-3.5 ${inConferma ? 'text-red-600' : 'text-textPrimary/50'}`}
                      />
                    </div>

                    <span className="flex-1 min-w-0 text-xs font-medium text-textPrimary truncate">
                      {alias}
                    </span>

                    {inConferma ? (
                      <div className="flex items-center gap-1.5 shrink-0">
                        <span className="text-[11px] text-red-700">Rimuovere?</span>
                        <button
                          type="button"
                          onClick={() => rimuovi(alias)}
                          disabled={sta}
                          className="px-2 py-1 rounded-lg text-[11px] font-medium bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50"
                        >
                          {sta ? '…' : 'Sì'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setDaRimuovere(null)}
                          disabled={sta}
                          className="px-2 py-1 rounded-lg text-[11px] font-medium bg-buttonPrimaryBg text-buttonPrimaryText hover:bg-buttonPrimaryHover transition-colors disabled:opacity-50"
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setDaRimuovere(alias)}
                        className="shrink-0 text-textMuted hover:text-red-500 transition-colors"
                        title="Rimuovi alias"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </motion.li>
                );
              })}
            </ul>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default AliasModal;
